/**
 * utils/formatLeadAnalytics.js
 */

import { formatDateDDMMYYYY } from "./commonHooks";

/**
 * -----------------------------------------
 * FULL DATE + TIME
 * -----------------------------------------
 */

const formatFullDateTime = (
  timestamp
) => {
  if (!timestamp) return "-";

  return new Date(
    timestamp
  ).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

/**
 * -----------------------------------------
 * TIME ONLY
 * -----------------------------------------
 */

const formatTime = (
  timestamp
) => {
  if (!timestamp) return "-";

  return new Date(
    timestamp
  ).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

/**
 * -----------------------------------------
 * DURATION FORMAT
 * -----------------------------------------
 */

const formatDuration = (
  seconds
) => {
  if (
    seconds === null ||
    seconds === undefined
  ) {
    return "0s";
  }

  const hrs = Math.floor(
    seconds / 3600
  );

  const mins = Math.floor(
    (seconds % 3600) / 60
  );

  const secs = seconds % 60;

  const parts = [];

  if (hrs)
    parts.push(`${hrs}h`);

  if (mins)
    parts.push(`${mins}m`);

  if (secs || parts.length === 0)
    parts.push(`${secs}s`);

  return parts.join(" ");
};

/**
 * -----------------------------------------
 * MAIN TRANSFORMER
 * -----------------------------------------
 */

export const transformLeadAnalytics =
  (analytics) => {
    return {
      /**
       * SESSION SUMMARY
       */

      summary: [
        {
          id: 1,

          sessionStart:
            formatFullDateTime(
              analytics.session
                ?.startedAt
            ),

          sessionEnd:
            formatFullDateTime(
              analytics.session
                ?.endedAt
            ),

          overallDuration:
            formatDuration(
              analytics.session
                ?.durationSeconds
            ),

          chatDuration:
            formatDuration(
              analytics.totals
                ?.totalChatTime
            ),

          projectsDuration:
            formatDuration(
              analytics.totals
                ?.totalProjectsTime
            ),
        },
      ],

      /**
       * VIEWS
       */

      views:
        analytics.views.map(
          (view, index) => ({
            id: index + 1,

            view:
              view.view,

            start:
              formatTime(
                view.startedAt
              ),

            end:
              formatTime(
                view.endedAt
              ),

            duration:
              formatDuration(
                view.durationSeconds
              ),
          })
        ),

      /**
       * FILTERS
       */

      filters:
        analytics.filters.map(
          (filter, index) => ({
            id: index + 1,

            filter:
              filter.filter,

            openedAt:
              formatTime(
                filter.openedAt
              ),

            closedAt:
              formatTime(
                filter.closedAt
              ),

            duration:
              formatDuration(
                filter.durationSeconds
              ),

            interactions:
              filter.interactions.map(
                (
                  interaction,
                  interactionIndex
                ) => ({
                  id:
                    interactionIndex + 1,

                  ...interaction,

                  timestamp:
                    formatTime(
                      interaction.timestamp
                    ),
                })
              ),
          })
        ),

      /**
       * PROPERTIES
       */

      properties:
        analytics.properties.map(
          (property, index) => ({
            id: index + 1,

            property:
              property.property,

            viewedAt:
              formatTime(
                property.viewedAt
              ),
          })
        ),
    };
  };